import { DictionaryRecordContextProvider } from "@/components/dictionaries/DictionaryContext";
import DictionaryLoader from "@/components/dictionaries/DictionaryLoader";
import DictionaryPageHeader from "@/components/dictionaries/DictionaryPageHeader";
import { useToast } from "@/components/ui/use-toast";
import { Module } from "@/constants";
import { DictionaryContext } from "@/containers/dictionaries/types";
import { useQueryCacheUpdater } from "@/hooks/useQueryCacheUpdater";
import { useFormUnsavedChangesWarning } from "@/hooks/useFormUnsavedChangesWarning";
import PayrollDepartmentForm from "@/modules/payrollDepartments/components/PayrollDepartmentForm";
import { usePayrollDepartmentsDictionary } from "@/modules/payrollDepartments/hooks/usePayrollDepartmentsDictionary";
import { ApiError } from "@/services/api";
import {
  ReadPayrollDepartment,
  WritePayrollDepartment,
} from "@/types/payrollDepartment";
import { zodResolver } from "@hookform/resolvers/zod";
import { MutationFunction, useMutation, useQuery } from "@tanstack/react-query";
import { exists } from "i18next";
import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useModulePermissions } from "@/modules/account/hooks/useModulePermissions";
import PayrollDepartmentEditorFooter from "@/modules/payrollDepartments/components/PayrollDepartmentEditorFooter";

export default function PayrollDepartmentEditor({ id }: DictionaryContext) {
  const { t } = useTranslation();
  const config = usePayrollDepartmentsDictionary();
  const navigate = useNavigate();
  const { toast } = useToast();
  const labels = config.getLabels(t);
  const { updateQueryDataForExistingItem } = useQueryCacheUpdater(
    Module.PayrollDepartments,
  );

  const { can_update } = useModulePermissions();
  const query = useQuery<ReadPayrollDepartment>({
    queryKey: [config.ns, config.id, id],
    queryFn: async () => {
      const result = await config.service.get(id);
      return result as ReadPayrollDepartment;
    },
  });

  const mutationFn: MutationFunction<
    ReadPayrollDepartment,
    WritePayrollDepartment
  > = async (values) => config.service.patch(id, values);

  const deleteMutationFn: MutationFunction<void, void> = async () =>
    config.service.delete(id);

  const deleteMutation = useMutation({
    mutationFn: deleteMutationFn,
    onSuccess: () => {
      navigate(`${config.path}`);
      toast({
        title: labels.success.delete,
      });
    },
    onError: () => {
      toast({
        title: labels.errors.delete,
      });
    },
  });

  const mutation = useMutation<
    ReadPayrollDepartment,
    ApiError,
    WritePayrollDepartment
  >({
    mutationFn,
    onSuccess: (response) => {
      const id = response.id;
      // Reset form with current values to clear dirty state This is needed because
      // ShiftReportDepartmentSelect loads data asynchronously, which updates the field after form
      // initialization and marks it as dirty. Without this reset, the Submit button would remain
      // enabled after successful save.
      form.reset(form.getValues());
      navigate(`${config.path}/${id}`);
      toast({
        title: labels.success.create,
      });
      updateQueryDataForExistingItem(response);
    },
    onError: ({ errorCode }) => {
      const description = exists(`errors.${errorCode}`)
        ? t(`errors.${errorCode}`)
        : t("errors.unknownError");
      toast({
        title: labels.errors.create,
        description,
        variant: "destructive",
      });
    },
  });

  const form = useForm<WritePayrollDepartment>({
    resolver: zodResolver(config.schema),
    defaultValues: config.defaultValues,
    disabled: mutation.isPending || deleteMutation.isPending || !can_update,
  });

  useFormUnsavedChangesWarning(form);

  useEffect(() => {
    if (query.data) {
      const formValues = {
        name: query.data.name || "",
        description: query.data.description || "",
        shift_report_department_id:
          query.data.shift_report_department_id || undefined,
        payroll_type_id: query.data.payroll_type_id || undefined,
      };

      form.reset(formValues);
    }
  }, [form, query.data]);

  return (
    <DictionaryRecordContextProvider
      value={{
        query,
        config,
        mutation,
        deleteMutation,
        form,
        id,
      }}
    >
      <DictionaryPageHeader />
      {query.isLoading && <DictionaryLoader />}
      {!query.isLoading && (
        <PayrollDepartmentForm footer={<PayrollDepartmentEditorFooter />} />
      )}
    </DictionaryRecordContextProvider>
  );
}
